import { useState, useEffect, useRef, Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/index';
import { NewProjectWizard } from './components/NewProjectWizard';
import { HomePage } from './components/HomePage/HomePage';
import { PageMeta } from './pages/PageMeta';
import { LEARN_LAZY_ROUTES } from './pages/learn/lazyLearnRoutes';
import {
  LazyTrackerDashboard,
  LazyPatternLibrary,
  LazyAddPatternChooser,
  LazyAboutPage,
  LazyPatternBuilder,
  LazySimplePatternWizard,
  LazySettings,
  LazyJustKnitWizard,
  LazySyncDetailsPage,
  LazyAllProjectsPage,
  LazyAiImportWizard,
} from './lazyRoutes';
import { BUILTIN_JUST_KNIT_ID, BUILTIN_JUST_KNIT_PATTERN } from './logic/builtin-patterns';
import { getFullState, savePattern } from './storage/persistence';
import { initAutoSync } from './storage/sync';
import { getSettings } from './storage/settings';
import { applyTheme } from './utils/theme';
import { setHapticsEnabled } from './utils/haptics';
import { generateDsl } from './logic/dsl-generator';
import { resolveSessionPattern } from './logic/sessionPattern';
import type { Session } from './types/session';
import type { Pattern } from './types/state';
import type { IRPattern } from './types/ir';
import { initAnalytics, track, captureException } from './analytics';
import { pathnameToView } from './analytics/viewMapping';
import type { ViewName } from './analytics/events';
import ConsentBanner from './components/ConsentBanner';
import SaveErrorBanner from './components/SaveErrorBanner';
import DurabilityBanner from './components/DurabilityBanner';
import RecoveryToast from './components/RecoveryToast';

// ---------------------------------------------------------------------------
// Error boundary — catches unexpected React render/lifecycle errors and
// reports them to Sentry via captureException.
// ---------------------------------------------------------------------------

class AnalyticsErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, { componentStack: info.componentStack ?? undefined });
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="min-h-dvh bg-base text-ink flex items-center justify-center p-8 text-center"
        >
          <div>
            <p className="text-lg font-semibold mb-2">Something went wrong.</p>
            <p className="text-ink-faint text-sm">Please reload the page to continue.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export { AnalyticsErrorBoundary };

const mockStructure: IRPattern = {
  id: 'root',
  kind: 'pattern',
  ir_version: '1.0',
  name: 'Classic Raglan Sweater',
  params: [],
  counters: [],
  segments: [
    {
      id: 'sec1',
      kind: 'segment',
      name: 'Neckline',
      counters: [],
      setup: [],
      body: [
        {
          id: 'grp_neck',
          kind: 'group',
          iter_name: 'i',
          count: { kind: 'count_fixed', expr: { kind: 'literal', type: 'int', value: 10 } },
          counters: [],
          body: [
            {
              id: 'row1',
              kind: 'row',
              instruction: {
                kind: 'instruction_text',
                expr: { kind: 'literal', type: 'text', value: 'K2, P2 Ribbing' },
              },
              deltas: [],
            },
          ],
        },
      ],
    },
    {
      id: 'sec2',
      kind: 'segment',
      name: 'Yoke',
      counters: [],
      setup: [],
      body: [
        {
          id: 'grp1',
          kind: 'group',
          iter_name: 'i',
          count: { kind: 'count_fixed', expr: { kind: 'literal', type: 'int', value: 20 } },
          counters: [],
          body: [
            {
              id: 'row2',
              kind: 'row',
              instruction: {
                kind: 'instruction_cases',
                cases: [
                  {
                    when: {
                      kind: 'binop',
                      op: '==',
                      left: {
                        kind: 'binop',
                        op: '%',
                        left: { kind: 'counter_ref', name: 'i' },
                        right: { kind: 'literal', type: 'int', value: 2 },
                      },
                      right: { kind: 'literal', type: 'int', value: 1 },
                    },
                    row: {
                      id: 'case_inc',
                      kind: 'row',
                      instruction: {
                        kind: 'instruction_text',
                        expr: { kind: 'literal', type: 'text', value: 'Knit all, inc 8 sts' },
                      },
                      deltas: [],
                    },
                  },
                ],
                else: {
                  id: 'case_else',
                  kind: 'row',
                  instruction: {
                    kind: 'instruction_text',
                    expr: { kind: 'literal', type: 'text', value: 'Knit all' },
                  },
                  deltas: [],
                },
              },
              deltas: [],
            },
          ],
        },
      ],
    },
    {
      id: 'sec3',
      kind: 'segment',
      name: 'Body',
      counters: [],
      setup: [],
      body: [
        {
          id: 'grp_body',
          kind: 'group',
          iter_name: 'i',
          count: { kind: 'count_fixed', expr: { kind: 'literal', type: 'int', value: 50 } },
          counters: [],
          body: [
            {
              id: 'row3',
              kind: 'row',
              instruction: {
                kind: 'instruction_text',
                expr: { kind: 'literal', type: 'text', value: 'Stockinette Stitch (Knit all)' },
              },
              deltas: [],
            },
          ],
        },
      ],
    },
  ],
};

const mockPattern: Pattern = {
  id: 'mock-classic-raglan',
  title: 'Classic Raglan Sweater',
  parentPatternId: null,
  dsl: generateDsl(mockStructure),
  structure: mockStructure,
};

function JustKnitRoute() {
  const navigate = useNavigate();
  return (
    <>
      <PageMeta
        title="Just Knit — Knit in Time"
        description="Quick session."
        path="/just-knit/"
        noindex
      />
      <LazyJustKnitWizard
        onCancel={() => navigate('/')}
        onProjectCreated={(sessionId) => navigate(`/tracker/${sessionId}`)}
      />
    </>
  );
}

function DashboardRoute() {
  return (
    <>
      <PageMeta
        title="Knit in Time — free knitting row counter, timer, and pattern manager"
        description="A mobile-friendly row counter, row timer, and pattern editor for knitters. Works offline on any phone. Free, no accounts."
        path="/"
        structuredData={{
          '@type': 'WebApplication',
          name: 'Knit in Time',
          description:
            'A mobile-friendly row counter, row timer, and pattern editor for knitters that works offline.',
          applicationCategory: 'LifestyleApplication',
          operatingSystem: 'Any',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          url: 'https://knitintime.com/',
          screenshot: 'https://knitintime.com/og-default.png',
        }}
      />
      <HomePage />
    </>
  );
}

function ProjectsRoute() {
  const navigate = useNavigate();
  return (
    <>
      <PageMeta
        title="Projects — Knit in Time"
        description="All projects."
        path="/projects/"
        noindex
      />
      <LazyAllProjectsPage
        onBack={() => navigate('/')}
        onSelectSession={(sessionId) => navigate(`/tracker/${sessionId}`)}
      />
    </>
  );
}

function SettingsRoute() {
  const navigate = useNavigate();
  return (
    <>
      <PageMeta
        title="Settings — Knit in Time"
        description="App settings."
        path="/settings/"
        noindex
      />
      <LazySettings onBack={() => navigate('/')} />
    </>
  );
}

function AboutRoute() {
  const navigate = useNavigate();
  return <LazyAboutPage onBack={() => navigate('/')} />;
}

function SyncDetailsRoute() {
  return (
    <>
      <PageMeta
        title="Sync details — Knit in Time"
        description="Sync conflict details."
        path="/sync-details/"
        noindex
      />
      <LazySyncDetailsPage />
    </>
  );
}

function LibraryRoute() {
  const navigate = useNavigate();
  const [startingFrom, setStartingFrom] = useState<Pattern | null>(null);

  return (
    <>
      <PageMeta
        title="Library — Knit in Time"
        description="Your pattern library."
        path="/library/"
        noindex
      />
      <LazyPatternLibrary
        onBack={() => navigate('/')}
        onAddPattern={() => navigate('/library/add/')}
        onEditPattern={(pattern) => navigate(`/builder/${pattern.id}`)}
        onSelectPattern={(pattern) => setStartingFrom(pattern)}
      />
      {startingFrom && (
        <NewProjectWizard
          pattern={startingFrom}
          onCancel={() => setStartingFrom(null)}
          onProjectCreated={(sessionId) => {
            setStartingFrom(null);
            navigate(`/tracker/${sessionId}`);
          }}
        />
      )}
    </>
  );
}

function AddPatternRoute() {
  const navigate = useNavigate();
  return (
    <>
      <PageMeta
        title="Add to your library — Knit in Time"
        description="Pick how to add a pattern to your library."
        path="/library/add/"
        noindex
      />
      <LazyAddPatternChooser onBack={() => navigate('/library/')} />
    </>
  );
}

function BuilderRoute() {
  const { patternId } = useParams<{ patternId?: string }>();
  // Key remounts the inner component when patternId changes, so initial state
  // is always correct without setState-in-effect for the no-pattern path.
  return <BuilderRouteImpl key={patternId ?? '__new__'} patternId={patternId} />;
}

function BuilderRouteImpl({ patternId }: { patternId?: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const [editingPattern, setEditingPattern] = useState<Pattern | undefined>(undefined);
  const [loading, setLoading] = useState(!!patternId);

  useEffect(() => {
    if (!patternId) return;
    let cancelled = false;
    (async () => {
      const state = await getFullState();
      if (cancelled) return;
      setEditingPattern(state.patterns[patternId]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [patternId]);

  if (loading) {
    return (
      <div className="p-8 text-center text-ink-faint">
        {t('loadingPattern', 'Loading pattern…')}
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Pattern Builder — Knit in Time"
        description="Edit a knitting pattern."
        path="/builder/"
        noindex
      />
      <LazyPatternBuilder
        initialPattern={editingPattern}
        onCancel={() => navigate('/library/')}
        onSave={async (pattern) => {
          await savePattern(pattern);
          navigate('/library/');
        }}
      />
    </>
  );
}

function WizardRoute() {
  const navigate = useNavigate();

  return (
    <>
      <PageMeta
        title="New Pattern Wizard — Knit in Time"
        description="Quickly create a knitting pattern."
        path="/wizard/"
        noindex
      />
      <LazySimplePatternWizard
        onCancel={() => navigate('/library/')}
        onSave={async (pattern) => {
          await savePattern(pattern);
          navigate('/library/');
        }}
      />
    </>
  );
}

function AiImportRoute() {
  const navigate = useNavigate();

  return (
    <>
      <PageMeta
        title="Import from AI — Knit in Time"
        description="Translate a pattern PDF into Knit in Time using your AI assistant."
        path="/ai-import/"
        noindex
      />
      <LazyAiImportWizard
        onCancel={() => navigate('/library/')}
        onImport={async (pattern) => {
          await savePattern(pattern);
          navigate('/library/');
        }}
      />
    </>
  );
}

function TrackerRoute() {
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<Session | null>(null);
  const [pattern, setPattern] = useState<Pattern | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      const state = await getFullState();
      if (cancelled) return;
      const s = state.sessions[sessionId] ?? null;
      const p = s ? (state.patterns[s.patternId] ?? null) : null;
      setSession(s);
      setPattern(p);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="p-8 text-center text-ink-faint">
        {t('loadingSession', 'Loading session…')}
      </div>
    );
  }

  const ir = session && pattern ? resolveSessionPattern(pattern, session) : undefined;

  if (!session || !pattern || !ir) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <PageMeta
        title="Tracker — Knit in Time"
        description="Active knitting session."
        path="/tracker/"
        noindex
      />
      <LazyTrackerDashboard
        pattern={ir}
        pdfRef={pattern.pdfRef}
        session={session}
        onBack={() => navigate('/')}
        onOpenSettings={() => navigate('/settings/')}
      />
    </>
  );
}

/**
 * Analytics side-effects that require router context (useLocation).
 * Mounted inside the App's router tree so hooks work correctly.
 */
export function AnalyticsEffects() {
  const location = useLocation();
  const prevView = useRef<ViewName | null>(null);

  // Boot analytics once on mount.
  useEffect(() => {
    void initAnalytics();
  }, []);

  // Fire app_opened once on first mount.
  useEffect(() => {
    const fromInstall =
      typeof window !== 'undefined' &&
      window.location.search.includes('source=pwa');
    track('app_opened', { from_install: fromInstall });
  }, []);

  // Listen for the PWA appinstalled event.
  useEffect(() => {
    const handler = () => track('pwa_installed', {});
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // Fire view_switched when the mapped view changes.
  useEffect(() => {
    const current = pathnameToView(location.pathname);
    if (current === null) return;
    const prev = prevView.current;
    if (prev !== null && prev !== current) {
      track('view_switched', { from: prev, to: current });
    }
    prevView.current = current;
  }, [location.pathname]);

  return null;
}

function App() {
  useEffect(() => {
    getSettings().then((s) => {
      applyTheme(s.theme ?? 'system');
      setHapticsEnabled(s.haptics);
      if (s.language && s.language !== i18n.language) {
        void i18n.changeLanguage(s.language);
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      const state = await getFullState();
      // Seed the canonical Just Knit pattern if missing. It's hidden from the
      // library/picker (builtin: true) and only used as the parent that every
      // Just Knit project forks from.
      if (!state.patterns[BUILTIN_JUST_KNIT_ID]) {
        await savePattern(BUILTIN_JUST_KNIT_PATTERN);
      }
      // First-run mock pattern: only seeded if the library is otherwise empty.
      const userPatternCount = Object.values(state.patterns).filter((p) => !p.builtin).length;
      if (userPatternCount === 0) {
        await savePattern(mockPattern);
      }
    })();
  }, []);

  useEffect(() => {
    return initAutoSync();
  }, []);

  return (
    <AnalyticsErrorBoundary>
      <div className="min-h-dvh bg-base text-ink transition-colors duration-300">
        <AnalyticsEffects />
        <SaveErrorBanner />
        <DurabilityBanner />
        <RecoveryToast />
        <ConsentBanner />
        <Routes>
          <Route path="/" element={<DashboardRoute />} />
          <Route path="/projects" element={<ProjectsRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/sync-details" element={<SyncDetailsRoute />} />
          <Route path="/about" element={<AboutRoute />} />
          <Route path="/library" element={<LibraryRoute />} />
          <Route path="/library/add" element={<AddPatternRoute />} />
          <Route path="/builder" element={<BuilderRoute />} />
          <Route path="/builder/:patternId" element={<BuilderRoute />} />
          <Route path="/wizard" element={<WizardRoute />} />
          <Route path="/just-knit" element={<JustKnitRoute />} />
          <Route path="/ai-import" element={<AiImportRoute />} />
          <Route path="/tracker/:sessionId" element={<TrackerRoute />} />
          {LEARN_LAZY_ROUTES.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          <Route path="/calendar" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AnalyticsErrorBoundary>
  );
}

export default App;
